import React, { useState, useEffect, useRef } from "react";
import tw, { styled, css } from "twin.macro";
import "styled-components/macro";
import _ from "lodash";
import { Link } from "gatsby";

import SocialMedia from "./SocialMedia";
import NavSubMenu from "./NavSubMenu";
import ScheduleSlider from "./ScheduleSlider";
import DynamicContainer from "./DynamicContainer";
import Icon from "./Icon";

import LocationSvg from "../images/location.svg";
import PhoneSvg from "../images/phone.svg";
import EmailSvg from "../images/email.svg";
import { useBreakpoint } from "gatsby-plugin-breakpoints";

const ScheduleContainer = styled(DynamicContainer)`
  @media (max-width: 640px) {
    ${tw`px-8`}
  }
`;

const Wrapper = styled.header`
  ${tw`
    bg-black
    sticky top-0 flex
    text-white
    min-h-[100px]
    z-30
  `}
`;

const Container = styled(DynamicContainer)`
  ${tw`
    grid relative grow
  `}
`;

const BottomBannerBg = styled.div`
  ${tw`
    absolute top-2/3
    w-full h-0 xl:h-1/3
    -z-10
  `}
  background-color: ${(props) => props.color};
`;

const Content = styled.div`
  ${tw`flex items-center`}
`;

const LogoLinkContainer = styled.div`
  ${tw`mr-4 my-2 absolute top-2`}
`;

const Checkbox = styled.input`
  ${tw`hidden`}
`;

const MenuIcon = styled.label`
  ${tw`
    absolute top-1/2 right-11 sm:right-14
    block xl:hidden 
    translate-x-6 px-6 
    -translate-y-4 py-4
  `}

  span {
    ${tw`
      absolute bg-white
      transition-all duration-200
      transform -translate-x-1/2 -translate-y-1/2 
    `}

    width: 3rem;
    height: 4.5px;

    ${Checkbox}:checked ~ & {
      ${tw`bg-transparent`}
    }

    &::before,
    &::after {
      ${tw`
        absolute bg-white
        transition-all duration-200
      `}

      content: "";
      width: 3rem;
      height: 4.5px;
    }

    &::before {
      ${tw`top-3`}

      ${Checkbox}:checked ~ & {
        ${tw`
          transform rotate-45 
          bg-white top-0
        `}
      }
    }

    &::after {
      ${tw`-top-3`}

      ${Checkbox}:checked ~ & {
        ${tw`
          transform -rotate-45 
           bg-white top-0
        `}
      }
    }
  }
`;

const Banners = styled.div`
  ${tw`
    flex flex-col grow
    h-screen xl:h-full 
    ml-[200px]
  `}

  @media (max-width: 1279px) {
    ${tw`
      overflow-y-auto w-0 bg-gray-800
      absolute top-0 right-0 
      origin-right
    `}

    &::-webkit-scrollbar {
      display: none;
    }

    & {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }

    ${({ dropdownOpen }) =>
      dropdownOpen &&
      tw`
        transition-[width] duration-500
        w-full sm:w-96
      `}
  }
`;

const NavContainer = styled.div`
  ${tw`xl:h-2/3 xl:w-full`}
`;

const Navigation = styled.nav`
  ${tw`
    h-full w-full 
    xl:inline-block
    text-gray-400 xl:text-white xl:text-sm 
  `}
`;

const NavList = styled.ul`
  ${tw`
    list-none
    flex flex-col 
    xl:flex-row xl:items-center xl:justify-between xl:gap-3
    xl:w-full xl:h-full
  `}
`;

const NavListItem = styled.li`
  ${tw`
    flex flex-col 
    text-left xl:relative 
    border-b-[1px] border-gray-400 xl:border-none  
  `}

  ${({ mobileOnly }) => mobileOnly && tw`block xl:hidden`}
  }
`;

const NavLinkContainer = styled.div`
  ${tw`
    w-full xl:w-auto
    flex items-center
    no-underline
  `}

  @media (max-width: 1279px) {
    ${tw`hover:text-white`}
  }
`;

const NavLink = styled(Link)`
  ${tw`
    flex items-center whitespace-nowrap
    cursor-pointer 

    w-full
  `}
  @media (min-width: 1280px) {
    &:hover {
      color: ${(props) => props.color};
    }
  }

  > span {
    ${tw`first-letter:underline decoration-gray-600`}
  }
`;

const ExternalNavLink = styled.a`
  ${tw`
    flex items-center whitespace-nowrap
    cursor-pointer 
    w-full
  `}
  @media (min-width: 1280px) {
    &:hover {
      color: ${(props) => props.color};
    }
  }

  > span {
    ${tw`first-letter:underline decoration-gray-600`}
  }
`;

const NavLinkNoNav = styled.div`
  ${tw`
    w-full
    transition-[color] duration-200
    flex items-center whitespace-nowrap
    cursor-pointer 
  `}
  @media (min-width: 1280px) {
    &:hover {
      color: ${(props) => props.color};
    }
  }

  @media (max-width: 1279px) {
    ${tw`p-4`}
  }

  > span {
    ${tw`first-letter:underline decoration-gray-600`}
  }
`;

const NavLinkExternal = styled.a`
  ${tw`
    w-full
    transition-[color] duration-200
    flex items-center whitespace-nowrap
    cursor-pointer
  `}
  @media (min-width: 1280px) {
    &:hover {
      color: ${(props) => props.color};
    }
  }
  > span {
    ${tw`first-letter:underline decoration-gray-600`}
  }
`;

const NavButton = styled.button`
  ${tw`
    inline-block w-full text-left
    transition-[background-color] duration-200
    xl:rounded-lg xl:bg-[#EC1F27] xl:hover:bg-[#EC1F27]/80
    xl:py-2 xl:px-4 
    xl:text-white xl:font-semibold
  `}
  @media (min-width: 1280px) {
    backgroundcolor: ${(props) => props.color};
    &:hover {
      opacity: 0.8;
    }
  }

  @media (max-width: 1279px) {
    ${tw`first-letter:underline decoration-gray-600 p-4`}
  }
`;

const NavSpan = styled.span`
  ${tw`
    inline-block w-full text-left
    xl:py-2 xl:px-4 
    xl:text-white
    cursor-pointer 
  `}

  @media (min-width: 1280px) {
    &:hover {
      color: ${(props) => props.color};
    }
  }

  @media (max-width: 1279px) {
    ${tw`first-letter:underline decoration-gray-600 p-4`}
  }
`;

const NavSubMenuContainer = styled.div`
  ${tw`
    absolute overflow-hidden
    filter xl:drop-shadow-2xl
    transform xl:-translate-x-1/2
    xl:invisible xl:opacity-0 
    xl:left-1/2 xl:top-full
  `}

  @media (min-width: 1280px) {
    &:hover,
    ${NavLinkContainer}:hover + & {
      ${tw`
        transition-opacity duration-300
        visible opacity-100
      `}
    }
  }

  @media (max-width: 1279px) {
    ${tw`
      transition-all duration-300
      w-full overflow-hidden 
      static opacity-0 
      origin-top transform scale-y-0
      max-h-0
    `}

    ${({ open }) =>
      open &&
      tw`
        transform scale-y-100 opacity-100
        max-h-full
      `}
  }
`;

const TeamInfo = styled.div`
  ${tw`
    m-4 mt-8 xl:m-0 xl:h-1/3
    flex flex-wrap items-center justify-around xl:justify-between
    gap-x-6 gap-y-3 xl:gap-y-0
    text-center xl:text-sm 
    font-bold
  `}

  @media (max-width: 1279px) {
    ${tw`opacity-0`}

    ${({ dropdownOpen }) =>
      dropdownOpen &&
      tw`
        transition-[opacity] delay-500 duration-200
        opacity-100
      `}
  }
`;
const TeamInfoTitle = styled.div`
  ${tw`text-[11px]`}
`;

const TeamInfoLink = styled(Link)`
  > button {
    ${tw`flex items-center text-[11px]`}
  }
`;

const SvgContainer = styled.div`
  ${tw`h-5 w-5 mr-0.5`}

  > svg {
    ${tw`fill-white`}
  }
`;

const TeamInfoAnchor = styled.a`
  ${tw`flex items-center text-[11px]`}
`;

const ChevronContainer = styled.div`
  ${tw`
    flex xl:hidden px-2
    text-gray-400 items-center
    cursor-default
  `}
  & > .material-icons {
    ${tw`text-4xl`}
  }
`;

const FilterContainer = styled.div`
  ${tw`absolute left-12 right-0 flex justify-center top-10`}
`;

const Nav = ({
  isHomePage,
  data,
  color,
  logoSrc,
  site,
  homeTeams,
  getNavData,
  socialMediaData,
  // renderTeamFilter,
  // for academy
  // dynamicTeamName = "",
  // dynamicScheduleName = "",
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({});

  const breakpoints = useBreakpoint();
  const isLessThanXL =
    !_.isEmpty(breakpoints) &&
    Object.values(breakpoints).reduce((a, b) => a + b) < 4;
  const showNavBurger = isLessThanXL;

  const navRef = useRef();

  useEffect(() => {
    if (!isLessThanXL) {
      setIsChecked(false);
    }
  }, [isLessThanXL]);

  const toggleDropdown = (key) => () => {
    let newState = { ...dropdownStates };
    newState[key] = !newState[key];
    setDropdownStates(newState);
  };

  function handleCheck(e) {
    setIsChecked((prevIsChecked) => !prevIsChecked);
  }

  const rosterPagePath = _.get(data, "latestRosterPage.nodes.0.path", "/");
  const schedulePagePath =
    site === "academy"
      ? "/schedule"
      : _.get(data, "latestSchedulePage.nodes.0.path", "/");
  const fmbaLeagueColor = _.get(
    data,
    "fmbaLeague.frontmatter.color",
    "#9561a9"
  );
  const navData = getNavData(rosterPagePath, {
    WCBLLink: _.get(data, "markdownRemark.frontmatter.WCBLLink"),
  });

  return (
    <>
      {isHomePage && (
        <ScheduleContainer>
          <ScheduleSlider
            data={data}
            site={site}
            color={color}
            homeTeams={homeTeams}
          />
        </ScheduleContainer>
      )}
      <Wrapper>
        <Container>
          <Content>
            <div>
              <LogoLinkContainer>
                <Link to="/">
                  <img
                    src={logoSrc}
                    alt="logo"
                    height={110}
                    style={{
                      width: "auto",
                      height: "110px",
                      objectFit: "contain",
                    }}
                    loading="lazy"
                  />
                </Link>
              </LogoLinkContainer>
              {/* {isLessThanXL && site === "academy" && (
                <FilterContainer>{renderTeamFilter()} </FilterContainer>
              )} */}
              {showNavBurger && (
                <div>
                  <Checkbox
                    type="checkbox"
                    id="menu"
                    checked={isChecked}
                    onChange={handleCheck}
                  />
                  <MenuIcon htmlFor="menu">
                    <span />
                  </MenuIcon>
                </div>
              )}
            </div>
            <Banners dropdownOpen={isChecked}>
              <NavContainer>
                <Navigation ref={navRef}>
                  <NavList>
                    <NavListItem mobileOnly>
                      <NavLinkContainer onClick={handleCheck} color={color}>
                        <NavLinkNoNav color={color}>
                          <span>Back</span>
                        </NavLinkNoNav>
                      </NavLinkContainer>
                    </NavListItem>
                    {site === "dawgs" && (
                      <NavListItem>
                        <NavLinkContainer tw="xl:py-0" color={color}>
                          <ExternalNavLink
                            href="https://tickets.dawgsbaseball.ca/"
                            color={color}
                          >
                            <NavButton color={color} onClick={handleCheck}>
                              Tickets
                            </NavButton>
                          </ExternalNavLink>
                          <ChevronContainer onClick={toggleDropdown("team")}>
                            <Icon
                              name={
                                dropdownStates["team"]
                                  ? "expand_less"
                                  : "expand_more"
                              }
                            />
                          </ChevronContainer>
                        </NavLinkContainer>
                        <NavSubMenuContainer open={dropdownStates["team"]}>
                          <NavSubMenu
                            hoveredLink={navData["team"]}
                            onClick={handleCheck}
                            color={color}
                          />
                        </NavSubMenuContainer>
                      </NavListItem>
                    )}

                    {site === "academy" && (
                      <NavListItem>
                        <NavLinkContainer tw="xl:py-0" color={color}>
                          <NavLinkExternal
                            href="https://dawgsbaseball.ca/"
                            target="_blank"
                            color={color}
                          >
                            <NavButton onClick={handleCheck} color={color}>
                              Collegiate Dawgs
                            </NavButton>
                          </NavLinkExternal>
                        </NavLinkContainer>
                      </NavListItem>
                    )}
                    <NavListItem>
                      <NavLinkContainer color={color}>
                        {site === "dawgs" && (
                          <>
                            <NavLinkNoNav color={color}>
                              <span>Team Info</span>
                            </NavLinkNoNav>
                            <ChevronContainer
                              onClick={toggleDropdown("teamInfo")}
                            >
                              <Icon
                                name={
                                  dropdownStates["teamInfo"]
                                    ? "expand_less"
                                    : "expand_more"
                                }
                              />
                            </ChevronContainer>
                          </>
                        )}
                        {site === "diamondbacks" && (
                          <NavLink to="/roster/2025" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Team Info
                            </NavSpan>
                          </NavLink>
                        )}
                        {site === "academy" && (
                          <NavLinkContainer>
                            <NavLink to="/program" color={color}>
                              <NavSpan onClick={handleCheck} color={color}>
                                Program
                              </NavSpan>
                            </NavLink>
                          </NavLinkContainer>
                        )}
                      </NavLinkContainer>
                      {site === "dawgs" && (
                        <NavSubMenuContainer open={dropdownStates["teamInfo"]}>
                          <NavSubMenu
                            hoveredLink={navData["teamInfo"]}
                            onClick={handleCheck}
                            color={color}
                          />
                        </NavSubMenuContainer>
                      )}
                    </NavListItem>
                    {site !== "academy" && (
                      <NavListItem>
                        <NavLinkContainer
                          onClick={toggleDropdown("schedule")}
                          color={color}
                        >
                          <NavLink to={schedulePagePath} color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Schedule
                            </NavSpan>
                          </NavLink>
                        </NavLinkContainer>
                        <NavSubMenuContainer open={dropdownStates["schedule"]}>
                          <NavSubMenu
                            hoveredLink={navData["schedule"]}
                            onClick={handleCheck}
                            color={color}
                          />
                        </NavSubMenuContainer>
                      </NavListItem>
                    )}
                    <NavListItem>
                      <NavLinkContainer>
                        <NavLink to="/facilities" color={color}>
                          <NavSpan onClick={handleCheck} color={color}>
                            Facilities
                          </NavSpan>
                        </NavLink>
                        <ChevronContainer
                          onClick={toggleDropdown("facilities")}
                        >
                          <Icon
                            name={
                              dropdownStates["facilities"]
                                ? "expand_less"
                                : "expand_more"
                            }
                          />
                        </ChevronContainer>
                      </NavLinkContainer>
                      <NavSubMenuContainer open={dropdownStates["facilities"]}>
                        <NavSubMenu
                          hoveredLink={navData["facilities"]}
                          onClick={handleCheck}
                          color={color}
                        />
                      </NavSubMenuContainer>
                    </NavListItem>
                    <NavListItem>
                      <NavLinkContainer>
                        {site === "diamondbacks" && (
                          <NavLink to="/fan-shop" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Fan Shop
                            </NavSpan>
                          </NavLink>
                        )}
                        {site === "dawgs" && (
                          <NavLinkExternal
                            href="https://dawgsgeneralstore.ca/"
                            target="_blank"
                            color={color}
                          >
                            <NavSpan onClick={handleCheck} color={color}>
                              Fan Shop
                            </NavSpan>
                          </NavLinkExternal>
                        )}
                        {site === "academy" && (
                          <NavLink to="/dawgs-coaching-staff" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Coaches
                            </NavSpan>
                          </NavLink>
                        )}
                      </NavLinkContainer>
                    </NavListItem>
                    {site === "academy" && (
                      <>
                        <NavListItem>
                          <NavLinkContainer>
                            <NavLinkNoNav
                              // to={`/roster/${
                              //   homeTeams.length > 1
                              //     ? ""
                              //     : _.toLower(
                              //         (homeTeams[0] || "").replace(/\s+/g, "-")
                              //       )
                              // }`}
                              color={color}
                            >
                              {/* <NavSpan onClick={handleCheck} color={color}>
                                {dynamicTeamName}
                              </NavSpan> */}
                              <span>Team</span>
                            </NavLinkNoNav>
                            <ChevronContainer
                              onClick={toggleDropdown("roster")}
                            >
                              <Icon
                                name={
                                  dropdownStates["roster"]
                                    ? "expand_less"
                                    : "expand_more"
                                }
                              />
                            </ChevronContainer>
                          </NavLinkContainer>
                          <NavSubMenuContainer open={dropdownStates["roster"]}>
                            <NavSubMenu
                              hoveredLink={navData["roster"]}
                              onClick={handleCheck}
                              color={color}
                            />
                          </NavSubMenuContainer>
                        </NavListItem>
                        <NavListItem>
                          <NavLinkContainer color={color}>
                            <NavLink to="/schedulelist" color={color}>
                              <NavSpan onClick={handleCheck} color={color}>
                                Schedule
                              </NavSpan>
                              {/* <span>Schedule</span> */}
                            </NavLink>
                            <ChevronContainer
                              onClick={toggleDropdown("schedule")}
                            >
                              <Icon
                                name={
                                  dropdownStates["schedule"]
                                    ? "expand_less"
                                    : "expand_more"
                                }
                              />
                            </ChevronContainer>
                          </NavLinkContainer>
                          <NavSubMenuContainer
                            open={dropdownStates["schedule"]}
                          >
                            <NavSubMenu
                              hoveredLink={navData["schedule"]}
                              onClick={handleCheck}
                              color={color}
                            />
                          </NavSubMenuContainer>
                        </NavListItem>
                        <NavListItem>
                          <NavLinkContainer color={color}>
                            <NavLink to="/alumni" color={color}>
                              <NavSpan onClick={handleCheck} color={color}>
                                Alumni
                              </NavSpan>
                            </NavLink>
                            <ChevronContainer
                              onClick={toggleDropdown("alumni")}
                            >
                              <Icon
                                name={
                                  dropdownStates["alumni"]
                                    ? "expand_less"
                                    : "expand_more"
                                }
                              />
                            </ChevronContainer>
                          </NavLinkContainer>
                          <NavSubMenuContainer open={dropdownStates["alumni"]}>
                            <NavSubMenu
                              hoveredLink={navData["alumni"]}
                              onClick={handleCheck}
                              color={color}
                            />
                          </NavSubMenuContainer>
                        </NavListItem>
                        <NavListItem>
                          <NavLinkContainer color={color}>
                            <>
                              <NavLinkNoNav color={color}>
                                <span>Become a Dawg</span>
                              </NavLinkNoNav>
                              <ChevronContainer
                                onClick={toggleDropdown("becomeDawg")}
                              >
                                <Icon
                                  name={
                                    dropdownStates["becomeDawg"]
                                      ? "expand_less"
                                      : "expand_more"
                                  }
                                />
                              </ChevronContainer>
                            </>
                          </NavLinkContainer>
                          <NavSubMenuContainer
                            open={dropdownStates["becomeDawg"]}
                          >
                            <NavSubMenu
                              hoveredLink={navData["becomeDawg"]}
                              onClick={handleCheck}
                              color={color}
                            />
                          </NavSubMenuContainer>
                        </NavListItem>
                      </>
                    )}
                    {site === "dawgs" && (
                      <NavListItem>
                        <NavLinkContainer>
                          {site === "diamondbacks" && (
                            <NavLink to="/news" color={color}>
                              <NavSpan onClick={handleCheck} color={color}>
                                News
                              </NavSpan>
                            </NavLink>
                          )}
                          {site === "dawgs" && (
                            <>
                              <NavLink to="/fan-experience" color={color}>
                                <NavSpan onClick={handleCheck} color={color}>
                                  Fan Experience
                                </NavSpan>
                              </NavLink>
                              <ChevronContainer
                                onClick={toggleDropdown("fan-experience")}
                              >
                                <Icon
                                  name={
                                    dropdownStates["fan-experience"]
                                      ? "expand_less"
                                      : "expand_more"
                                  }
                                />
                              </ChevronContainer>
                            </>
                          )}
                        </NavLinkContainer>

                        <NavSubMenuContainer
                          open={dropdownStates["fan-experience"]}
                        >
                          <NavSubMenu
                            hoveredLink={navData["fan-experience"]}
                            onClick={handleCheck}
                            color={color}
                          />
                        </NavSubMenuContainer>
                      </NavListItem>
                    )}
                    {site === "diamondbacks" && (
                      <NavListItem>
                        <NavLinkContainer>
                          <NavLink to="/news" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              News
                            </NavSpan>
                          </NavLink>
                        </NavLinkContainer>
                      </NavListItem>
                    )}
                    {site !== "academy" && (
                      <NavListItem>
                        <NavLinkContainer>
                          <NavLink to="/sponsors" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Sponsors
                            </NavSpan>
                          </NavLink>
                        </NavLinkContainer>
                      </NavListItem>
                    )}
                    <NavListItem>
                      <NavLinkContainer>
                        <NavLink to="/contact" color={color}>
                          <NavSpan onClick={handleCheck} color={color}>
                            Contact
                          </NavSpan>
                        </NavLink>
                      </NavLinkContainer>
                    </NavListItem>
                    {/* {site === "academy" && (
                      <NavListItem>
                        <NavLinkContainer>
                          <NavLink to="/media" color={color}>
                            <NavSpan onClick={handleCheck} color={color}>
                              Media
                            </NavSpan>
                          </NavLink>
                        </NavLinkContainer>
                      </NavListItem>
                    )} */}
                    {site === "dawgs" && (
                      <NavListItem>
                        <NavLinkContainer tw="xl:py-0" color={color}>
                          <NavLinkExternal
                            href="https://dawgsacademy.ca/"
                            target="_blank"
                            color={color}
                          >
                            <NavButton onClick={handleCheck} color={color}>
                              Dawgs Academy
                            </NavButton>
                          </NavLinkExternal>
                        </NavLinkContainer>
                      </NavListItem>
                    )}
                  </NavList>
                </Navigation>
              </NavContainer>
              <TeamInfo dropdownOpen={isChecked}>
                {site === "dawgs" && (
                  <>
                    <TeamInfoTitle>
                      {_.get(data, "markdownRemark.frontmatter.navText", "")}
                    </TeamInfoTitle>
                    <TeamInfoLink to="/directions/seaman-stadium">
                      <button onClick={handleCheck}>
                        <SvgContainer>
                          <LocationSvg />
                        </SvgContainer>
                        Seaman Stadium
                      </button>
                    </TeamInfoLink>
                    <TeamInfoAnchor href="tel:14032623294">
                      <SvgContainer>
                        <PhoneSvg />
                      </SvgContainer>
                      (403) 262-3294
                    </TeamInfoAnchor>
                    <TeamInfoAnchor href="mailto:info@dawgsbaseball.ca">
                      <SvgContainer>
                        <EmailSvg />
                      </SvgContainer>
                      info@dawgsbaseball.ca
                    </TeamInfoAnchor>
                  </>
                )}
                {site === "diamondbacks" && (
                  <>
                    <div>FMBA Champs 16,18,20,21,22,23,24</div>
                    <TeamInfoLink to="/directions/glen-hansen-field/">
                      <button onClick={handleCheck}>
                        <SvgContainer>
                          <LocationSvg />
                        </SvgContainer>
                        Glen Hanson Field
                      </button>
                    </TeamInfoLink>
                    <TeamInfoAnchor href="tel:14038162516">
                      <SvgContainer>
                        <PhoneSvg />
                      </SvgContainer>
                      (403) 816-2516
                    </TeamInfoAnchor>
                    <TeamInfoAnchor href="mailto:info@calgarydiamondbacks.com">
                      <SvgContainer>
                        <EmailSvg />
                      </SvgContainer>
                      info@calgarydiamondbacks.com
                    </TeamInfoAnchor>
                  </>
                )}
                {site === "academy" && (
                  <>
                    {/* {!isLessThanXL && renderTeamFilter()} */}
                    <TeamInfoLink to="/directions/seaman-stadium">
                      <button onClick={handleCheck}>
                        <SvgContainer>
                          <LocationSvg />
                        </SvgContainer>
                        Seaman Stadium
                      </button>
                    </TeamInfoLink>
                    <TeamInfoAnchor href="tel:14032623294">
                      <SvgContainer>
                        <PhoneSvg />
                      </SvgContainer>
                      (403) 995-3241
                    </TeamInfoAnchor>
                    <TeamInfoAnchor href="mailto:info@dawgsbaseball.ca">
                      <SvgContainer>
                        <EmailSvg />
                      </SvgContainer>
                      info@dawgsbaseball.ca
                    </TeamInfoAnchor>
                  </>
                )}
                <SocialMedia
                  iconColor="white"
                  noMargin
                  site={site}
                  socialMediaData={socialMediaData}
                />
              </TeamInfo>
            </Banners>
          </Content>
        </Container>
        <BottomBannerBg color={color} />
      </Wrapper>
    </>
  );
};

export default Nav;
